import React, { useState } from "react";
import { Label, Text, Dropdown, Option, makeStyles } from "@fluentui/react-components";
import { Dismiss16Regular } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import SearchResourceInGraph from "../../GenericComponent/SearchResourceInGraph";
import QueueSelectorComponent from "../../GenericComponent/QueueSelectorComponent";
import "./CSS/DaysComponent.css";
const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
  },
  section: { 
    marginTop: "1rem", 
    display: "flex", 
    flexDirection: "column", 
    gap: "0.5rem" 
  },
  input: { 
    padding: "4px 8px", 
    borderRadius: "4px", 
    border: "none", 
    width: "200px",
    backgroundColor: "#f3f2f1"
  },
  selectedDaysContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "0.5rem",
    marginTop: "0.5rem",
  },
  dayTag: {
    display: "flex",
    alignItems: "center",
    gap: "0.3rem",
    padding: "0.3rem 0.6rem",
    backgroundColor: "#e0f0ff",
    borderRadius: "4px",
    fontSize: "0.875rem",
  },
  removeButton: {
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
  },
});
const DAYS_OF_WEEK = [
  { value: "monday", labelKey: "monday" },
  { value: "tuesday", labelKey: "tuesday" },
  { value: "wednesday", labelKey: "wednesday" },
  { value: "thursday", labelKey: "thursday" },
  { value: "friday", labelKey: "friday" },
  { value: "saturday", labelKey: "saturday" },
  { value: "sunday", labelKey: "sunday" },
];
export default function DaysComponent({ data, routingName, onChange, setOnMatched, setOnUnMatched, routingList, permissions, isEditMode }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const [selectedDays, setSelectedDays] = useState(data?.daysList || []);
  const [matchedAction, setMatchedAction] = useState(data.onMatched?.type || "");
  const [unmatchedAction, setUnmatchedAction] = useState(data.onUnMatched?.type || "");
  const [selectedMatchedQueue, setSelectedMatchedQueue] = useState({
    displayName: data.onMatched?.queueDisplayName || "",
    id: data.onMatched?.queueId || "",
  });
  const [selectedMatchedUser, setSelectedMatchedUser] = useState({
    displayName: data.onMatched?.userDisplayName || "",
    id: data.onMatched?.targetId || "",
  });
  const [selectedMatchedRouting, setSelectedMatchedRouting] = useState(data.onMatched?.routingName || "");
  const [matchedPhone, setMatchedPhone] = useState(
    data.onMatched?.type === "transfer" && data.onMatched?.targetType === "phone" ? data.onMatched?.targetId || "" : ""
  );
  const [selectedUnmatchedQueue, setSelectedUnmatchedQueue] = useState({
    displayName: data.onUnMatched?.queueDisplayName || "",
    id: data.onUnMatched?.queueId || "",
  });
  const [selectedUnmatchedUser, setSelectedUnmatchedUser] = useState({
    displayName: data.onUnMatched?.userDisplayName || "",
    id: data.onUnMatched?.targetId || "",
  });
  const [selectedUnmatchedRouting, setSelectedUnmatchedRouting] = useState(data.onUnMatched?.routingName || "");
  const [unmatchedPhone, setUnmatchedPhone] = useState(
    data.onUnMatched?.type === "transfer" && data.onUnMatched?.targetType === "phone" ? data.onUnMatched?.targetId || "" : ""
  );
  const NEXT_ACTIONS = [
    { value: "queue", textKey: "queue" },
    { value: "transferUser", textKey: "transferToUser" },
    { value: "disconnect", textKey: "disconnect" },
    { value: "routing", textKey: "routing" },
    { value: "transferPhone", textKey: "transferToPhone" },
  ];

  const getTranslatedValue = (type, targetType) => {
    let value = type;
    if (type === "transfer") {
      value = targetType === "phone" ? "transferPhone" : "transferUser";
    }
    return t(NEXT_ACTIONS.find(a => a.value === value)?.textKey || value);
  };
  const handleDaySelect = (event, data) => {
    const day = data.optionValue;
    if (!selectedDays.includes(day)) {
      const updatedDays = [...selectedDays, day];
      setSelectedDays(updatedDays);
      onChange(routingName, "daysList", updatedDays);
    }
  };
  const handleDayRemove = (dayToRemove) => {
    const updatedDays = selectedDays.filter(d => d !== dayToRemove);
    setSelectedDays(updatedDays);
    onChange(routingName, "daysList", updatedDays);
  };
  const getAvailableDays = () => {
    return DAYS_OF_WEEK.filter(day => !selectedDays.includes(day.value));
  };
  const getDayLabel = (dayValue) => {
    return DAYS_OF_WEEK.find(d => d.value === dayValue)?.labelKey || dayValue;
  };
  const handleNextAction = (value, type) => {
    const isMatched = type === "matched";
    const saveFn = isMatched ? setOnMatched : setOnUnMatched;

    // Reset everything
    if (isMatched) {
      setSelectedMatchedQueue({ displayName: "", id: "" });
      setSelectedMatchedUser({ displayName: "", id: "" });
      setSelectedMatchedRouting("");
      setMatchedPhone("");
      const actionType = (value === "transferUser" || value === "transferPhone") ? "transfer" : value;
      setMatchedAction(actionType);
    } else {
      setSelectedUnmatchedQueue({ displayName: "", id: "" });
      setSelectedUnmatchedUser({ displayName: "", id: "" });
      setSelectedUnmatchedRouting("");
      setUnmatchedPhone("");
      const actionType = (value === "transferUser" || value === "transferPhone") ? "transfer" : value;
      setUnmatchedAction(actionType);
    }

    let obj = {};
    if (value === "transferUser") {
      obj = { type: "transfer", targetType: "user" };
    } else if (value === "transferPhone") {
      obj = { type: "transfer", targetType: "phone" };
    } else {
      obj = { type: value };
    }
    
    saveFn(obj);
  };

  return (
    <div className={classes.container}>
      {/* Days Selection */}
      <div className="days-row">
        <label className="days-label">{t("selectDays")}</label>
        <Dropdown
          className="days-dropdown"
          placeholder={t("selectDays")}
          disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
          onOptionSelect={handleDaySelect}
        >
          {getAvailableDays().map((day) => (
            <Option key={day.value} value={day.value}>
              {t(day.labelKey)}
            </Option>
          ))}
        </Dropdown>
      </div>

      {/* Selected Days Tags */}
      {selectedDays.length > 0 && (
        <div className={classes.selectedDaysContainer} style={{ marginLeft: '166px' }}>
          {selectedDays.map((dayValue) => (
            <div key={dayValue} className={classes.dayTag}>
              <span>{t(getDayLabel(dayValue))}</span>
              <div 
                className={classes.removeButton}
                onClick={() => !isEditMode || permissions?.routing?.canUpdate ? handleDayRemove(dayValue) : null}
              >
                <Dismiss16Regular />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Matched */}
      <div className={classes.section}>
        <Label>{t("ifMatched") || "Si correspond"}</Label>
        <Dropdown
          className="days-dropdown"
          value={getTranslatedValue(data.onMatched?.type, data.onMatched?.targetType)}
          disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
          onOptionSelect={(_, data) => handleNextAction(data.optionValue, "matched")}
          placeholder={t("selectAction") || "Select action"}
        >
          {NEXT_ACTIONS.map((opt) => (
            <Option key={opt.value} value={opt.value} text={t(opt.textKey)}>
              {t(opt.textKey)}
            </Option>
          ))}
        </Dropdown>
        {matchedAction === "queue" && (
          <QueueSelectorComponent
            displayName={selectedMatchedQueue.displayName}
            disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
            getData={(queue) => {
              setSelectedMatchedQueue(queue);
              setOnMatched({
                type: "queue",
                queueDisplayName: queue.displayName,
                queueId: queue.id,
              });
            }}
          />
        )}
        {(matchedAction === "transferUser" || (matchedAction === "transfer" && data.onMatched?.targetType === "user")) && (
          <SearchResourceInGraph
            displayName={selectedMatchedUser.displayName}
            disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
            onSelectUser={(user) => {
              setSelectedMatchedUser(user);
              setOnMatched({
                type: "transfer",
                userDisplayName: user.displayName,
                targetId: user.id,
                targetType: "user"
              });
            }}
          />
        )}
        {matchedAction === "routing" && (
          <Dropdown
            className="days-dropdown"
            placeholder={t("selectRouting")}
            value={selectedMatchedRouting}
            disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
            onOptionSelect={(_, option) => {
              setSelectedMatchedRouting(option.optionValue);
              setOnMatched({ type: "routing", routingName: option.optionValue });
            }}
          >
            {Object.keys(routingList || {})
              .filter((r) => r !== routingName)
              .map((r) => (
                <Option key={r} value={r}>
                  {routingList[r]?.displayName || r}
                </Option>
              ))}
          </Dropdown>
        )}
        {(matchedAction === "transferPhone" || (matchedAction === "transfer" && data.onMatched?.targetType === "phone")) && (
          <input
            type="tel"
            placeholder={t("enterPhoneNumber")}
            className={classes.input}
            value={matchedPhone}
            disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
            onChange={(e) => {
              setMatchedPhone(e.target.value);
              setOnMatched({ type: "transfer", targetId: e.target.value, targetType: "phone" });
            }}
          />
        )}
      </div>

      {/* Unmatched */}
      <div className={classes.section}>
        <Label>{t("ifNotMatched") || "Sinon"}</Label>
        <Dropdown
          className="days-dropdown"
          value={getTranslatedValue(data.onUnMatched?.type, data.onUnMatched?.targetType)}
          disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
          onOptionSelect={(_, data) => handleNextAction(data.optionValue, "unmatched")}
          placeholder={t("selectAction") || "Select action"}
        >
          {NEXT_ACTIONS.map((opt) => (
            <Option key={opt.value} value={opt.value} text={t(opt.textKey)}>
              {t(opt.textKey)}
            </Option>
          ))}
        </Dropdown>
        {unmatchedAction === "queue" && (
          <QueueSelectorComponent
            displayName={selectedUnmatchedQueue.displayName}
            disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
            getData={(queue) => {
              setSelectedUnmatchedQueue(queue);
              setOnUnMatched({
                type: "queue",
                queueDisplayName: queue.displayName,
                queueId: queue.id,
              });
            }}
          />
        )}
        {(unmatchedAction === "transferUser" || (unmatchedAction === "transfer" && data.onUnMatched?.targetType === "user")) && (
          <SearchResourceInGraph
            displayName={selectedUnmatchedUser.displayName}
            disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
            onSelectUser={(user) => {
              setSelectedUnmatchedUser(user);
              setOnUnMatched({
                type: "transfer",
                userDisplayName: user.displayName,
                targetId: user.id,
                targetType: "user"
              });
            }}
          />
        )}
        {unmatchedAction === "routing" && (
          <Dropdown
            className="days-dropdown"
            placeholder={t("selectRouting")}
            value={selectedUnmatchedRouting}
            disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
            onOptionSelect={(_, option) => {
              setSelectedUnmatchedRouting(option.optionValue);
              setOnUnMatched({ type: "routing", routingName: option.optionValue });
            }}
          >
            {Object.keys(routingList || {})
              .filter((r) => r !== routingName)
              .map((r) => (
                <Option key={r} value={r}>
                  {routingList[r]?.displayName || r}
                </Option>
              ))}
          </Dropdown>
        )}
        {(unmatchedAction === "transferPhone" || (unmatchedAction === "transfer" && data.onUnMatched?.targetType === "phone")) && (
          <input
            type="tel"
            placeholder={t("enterPhoneNumber")}
            className={classes.input}
            value={unmatchedPhone}
            disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
            onChange={(e) => {
              setUnmatchedPhone(e.target.value);
              setOnUnMatched({ type: "transfer", targetId: e.target.value, targetType: "phone" });
            }}
          />
        )}
      </div>
    </div>
  );
}